import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Language, LanguageService, ThemingService, languages } from '@catch/services';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faBrightnessLow,
  faDisplay,
  faGlobe,
  faMoon,
  faSignOutAlt,
} from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgSelectModule } from '@ng-select/ng-select';
import { PlatformAuthService } from '../../services/platform-auth.service';

type PopoverCloseEvent = Pick<Event, 'preventDefault' | 'stopPropagation'>;

@Component({
  selector: 'app-platform-sidenav-user',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FontAwesomeModule, TranslocoPipe, NgSelectModule, FormsModule],
  template: `
    <div class="ct-card p-0">
      <ul class="dropdown-menu">
        <li>
          <fa-icon [icon]="icons.globe"></fa-icon>
          <ng-select
            [placeholder]="'jobboard.platform.topnav.language' | transloco"
            [items]="availableLanguages()"
            [bindLabel]="'name'"
            [bindValue]="'value'"
            [ngModel]="languageService.currentLanguage?.value"
            (change)="setLanguage($event)"
            [searchable]="false"
            [clearable]="false"
          >
          </ng-select>
        </li>
        <li (click)="toggleTheme()">
          @switch (themingService.mode) {
            @case ('auto') {
              <fa-icon [icon]="icons.display"></fa-icon>
            }
            @case ('light') {
              <fa-icon [icon]="icons.brightnessLow"></fa-icon>
            }
            @case ('dark') {
              <fa-icon [icon]="icons.moon"></fa-icon>
            }
          }
          <p>{{ 'jobboard.platform.topnav.appearance' | transloco }}</p>
        </li>
        <hr />
        <li (click)="logout($event)">
          <fa-icon [icon]="icons.signOut"></fa-icon>
          <p>{{ 'jobboard.platform.sidenav.logout' | transloco }}</p>
        </li>
      </ul>
    </div>
  `,
  styleUrl: './platform-sidenav-user.component.scss',
})
export class PlatformSidenavUserComponent {
  readonly closePopover = input<((event: PopoverCloseEvent) => void) | undefined>();

  readonly icons = {
    globe: faGlobe,
    display: faDisplay,
    brightnessLow: faBrightnessLow,
    moon: faMoon,
    signOut: faSignOutAlt,
  };

  readonly themingService = inject(ThemingService);
  readonly languageService = inject(LanguageService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly auth = inject(PlatformAuthService);

  readonly availableLanguages = signal<Language[]>(
    languages.filter((lang) => lang.value !== 'fr-FR')
  );

  /**
   * Cycles theme mode: auto → light → dark → auto.
   */
  toggleTheme(): void {
    this.themingService.toggleTheme();
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Ends the session and navigates to the public jobboard.
   * @param event - Optional click event
   */
  logout(event?: Event): void {
    this.dismissPopover(event);
    this.auth.logout();
    this.router.navigate(['/']);
  }

  /**
   * Sets the UI language.
   * @param value - Language code
   */
  setLanguage(value: string | null): void {
    if (!value) {
      return;
    }
    this.languageService.setLanguage(value);
  }

  /**
   * Closes the parent popover.
   * @param event - Optional click event
   */
  dismissPopover(event?: Event): void {
    const closeEvent = event ?? this.createFallbackCloseEvent();
    this.closePopover()?.(closeEvent);
  }

  /**
   * Creates a fallback event for popover dismissal.
   * @returns Minimal event-like object
   */
  private createFallbackCloseEvent(): PopoverCloseEvent {
    return {
      preventDefault: () => undefined,
      stopPropagation: () => undefined,
    };
  }
}
